/**
 * src/voices/HeartbeatVoice.js
 *
 * Conveys water level via a rhythmic lub-dub heartbeat.
 *
 * Each heartbeat consists of two thumps synthesised with a pitched sine
 * oscillator whose frequency sweeps downward — the classic analogue kick/thud
 * technique. No samples required.
 *
 * Data mapping  ratio ∈ [0, 1]
 *   0 (empty) → ~40 BPM, low pitch  — slow, deep, relaxed
 *   1 (full)  → ~160 BPM, higher pitch — rapid, urgent
 *
 * The scheduler uses Web Audio's clock (ctx.currentTime) for drift-free
 * timing. A lookahead loop re-schedules thumps ~150 ms in advance so that
 * setInterval jitter never causes missed beats.
 */

import { lerp, clamp01 } from "../utils/math.js";

export class HeartbeatVoice {
    // ── static contract ───────────────────────────────────────────────────────

    static defaultParams() {
        return {
            bpmMin: 40,
            bpmMax: 200,
            freqStartMin: 55,
            freqStartMax: 140,
            freqEndMin: 28,
            freqEndMax: 55,
            punchDur: 0.12,
            peakGain: 0.45,
            lubDubGap: 0.18,
            scheduleAhead: 0.15,
        };
    }

    static meta = {
        label: "Heartbeat",
        liveLabel: "Water Level",
        liveDesc: "0 = empty   1 = full",
        liveDefault: 0.5,
        curveXLabel: "water level",
        curves(p) {
            return [
                {
                    key: "bpm",
                    label: "BPM (norm)",
                    color: "#f87171",
                    fn: (r) =>
                        lerp(p.bpmMin, p.bpmMax, r) / Math.max(p.bpmMax, 1),
                },
                {
                    key: "pitch",
                    label: "Pitch Start (norm)",
                    color: "#fbbf24",
                    fn: (r) =>
                        lerp(p.freqStartMin, p.freqStartMax, r) /
                        Math.max(p.freqStartMax, 1),
                },
            ];
        },
    };

    // ── constructor ───────────────────────────────────────────────────────────

    constructor(ctx, destination, cfg = {}) {
        this.ctx = ctx;
        const def = HeartbeatVoice.defaultParams();
        this.cfg = { ...def, ...cfg };

        this.masterGain = ctx.createGain();
        this.masterGain.gain.setValueAtTime(0, ctx.currentTime);
        this.masterGain.connect(destination);

        this._ratio = 0;
        this._alive = false;
        this._nextBeatTime = null;
        this._scheduleTimer = null;
    }

    // ── internal helpers ──────────────────────────────────────────────────────

    _beatPeriod(ratio) {
        const bpm = lerp(this.cfg.bpmMin, this.cfg.bpmMax, clamp01(ratio));
        return 60 / bpm;
    }

    _scheduleThump(when, ratio, isDub = false) {
        const r = clamp01(ratio);
        const { cfg } = this;

        const freqStart =
            lerp(cfg.freqStartMin, cfg.freqStartMax, r) * (isDub ? 0.8 : 1.0);
        const freqEnd =
            lerp(cfg.freqEndMin, cfg.freqEndMax, r) * (isDub ? 0.8 : 1.0);
        const gain = cfg.peakGain * (isDub ? 0.65 : 1.0);
        const punch = cfg.punchDur;

        const osc = this.ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freqStart, when);
        osc.frequency.exponentialRampToValueAtTime(
            Math.max(freqEnd, 1),
            when + punch,
        );

        const env = this.ctx.createGain();
        env.gain.setValueAtTime(0, when);
        env.gain.linearRampToValueAtTime(gain, when + 0.005);
        env.gain.exponentialRampToValueAtTime(0.001, when + punch);

        osc.connect(env);
        env.connect(this.masterGain);
        osc.start(when);
        osc.stop(when + punch + 0.02);

        const cleanupMs = Math.max(
            0,
            (when - this.ctx.currentTime + punch + 0.05) * 1000,
        );
        setTimeout(() => {
            try {
                env.disconnect();
            } catch (_) {}
        }, cleanupMs);
    }

    _runScheduler() {
        if (!this._alive) return;
        const horizon = this.ctx.currentTime + this.cfg.scheduleAhead;
        while (this._nextBeatTime < horizon) {
            this._scheduleThump(this._nextBeatTime, this._ratio, false);
            this._scheduleThump(
                this._nextBeatTime + this.cfg.lubDubGap,
                this._ratio,
                true,
            );
            this._nextBeatTime += this._beatPeriod(this._ratio);
        }
    }

    // ── public interface ──────────────────────────────────────────────────────

    start(ratio = 0.5) {
        this._ratio = clamp01(ratio);
        this._alive = true;
        this._nextBeatTime = this.ctx.currentTime + 0.05;

        const g = this.masterGain.gain,
            now = this.ctx.currentTime;
        g.cancelScheduledValues(now);
        g.setValueAtTime(0, now);
        g.linearRampToValueAtTime(1.0, now + 0.08);

        this._scheduleTimer = setInterval(() => this._runScheduler(), 50);
        this._runScheduler();
    }

    update(ratio) {
        // Tempo change takes effect on the next beat — no clicks
        this._ratio = clamp01(ratio);
    }

    stop(onDone) {
        this._alive = false;
        clearInterval(this._scheduleTimer);

        const now = this.ctx.currentTime;
        const fadeOut = 0.25;
        const g = this.masterGain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(0, now + fadeOut);

        setTimeout(
            () => {
                this.dispose();
                onDone?.();
            },
            (fadeOut + 0.05) * 1000,
        );
    }

    dispose() {
        this._alive = false;
        clearInterval(this._scheduleTimer);
        try {
            this.masterGain.disconnect();
        } catch (_) {}
    }
}
